import { Check, ChevronDown, Loader2 } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { levenshteinDistance } from "src/common/utils";
import { Input } from "src/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "src/components/ui/popover";

import type { GithubWorkflow } from "../types";

interface GithubWorkflowTypeaheadProps {
  workflows: GithubWorkflow[];
  value: string;
  onChange: (workflowId: string) => void;
  isLoading: boolean;
  disabled?: boolean;
  placeholder?: string;
  emptyMessage?: string;
}

interface WorkflowResultsListProps {
  workflows: GithubWorkflow[];
  selectedWorkflowId: string;
  onSelect: (workflow: GithubWorkflow) => void;
  emptyMessage: string;
}

const WorkflowResultsList = React.memo<WorkflowResultsListProps>(function WorkflowResultsList({
  workflows,
  selectedWorkflowId,
  onSelect,
  emptyMessage,
}) {
  if (workflows.length === 0) {
    return (
      <div className="p-3 text-xs text-muted-foreground">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="p-0.5">
      {workflows.map((workflow) => (
        <button
          key={workflow.id}
          onClick={() => onSelect(workflow)}
          className={`
            w-full flex items-center justify-between px-2 py-1.5 text-xs rounded
            hover:bg-blue-950/40 transition-colors text-left
            ${selectedWorkflowId === workflow.id.toString() ? "bg-card" : ""}
          `}
        >
          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            <span className="truncate text-foreground">{workflow.name}</span>
          </div>
          {selectedWorkflowId === workflow.id.toString() && (
            <Check className="h-3 w-3 text-primary shrink-0" />
          )}
        </button>
      ))}
    </div>
  );
});

export function GithubWorkflowTypeahead({
  workflows,
  value,
  onChange,
  isLoading,
  disabled = false,
  placeholder = "Select a workflow...",
  emptyMessage = "No workflows found matching your search.",
}: GithubWorkflowTypeaheadProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [triggerWidth, setTriggerWidth] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter out non-usable workflows (e.g., those without workflow_dispatch)
  const usableWorkflows = useMemo(() => {
    return workflows.filter((w) => w.is_usable !== false);
  }, [workflows]);

  const filteredWorkflows = useMemo(() => {
    if (!searchQuery) {
      // If no search query, show selected workflow first if it exists
      if (value) {
        const selected = usableWorkflows.find((w) => w.id.toString() === value);
        if (selected) {
          const others = usableWorkflows.filter((w) => w.id.toString() !== value);
          return [selected, ...others.sort((a, b) => a.name.localeCompare(b.name))];
        }
      }
      return usableWorkflows.slice().sort((a, b) => a.name.localeCompare(b.name));
    }

    const query = searchQuery.toLowerCase();

    // Filter workflows that contain the query
    const matchingWorkflows = usableWorkflows.filter((w) =>
      w.name.toLowerCase().includes(query)
    );

    // Sort by edit distance
    return matchingWorkflows.sort((a, b) => {
      const distanceA = levenshteinDistance(query, a.name.toLowerCase());
      const distanceB = levenshteinDistance(query, b.name.toLowerCase());

      if (distanceA === distanceB) {
        return a.name.localeCompare(b.name);
      }

      return distanceA - distanceB;
    });
  }, [usableWorkflows, searchQuery, value]);

  // Show top 10 results
  const limitedWorkflows = useMemo(() => filteredWorkflows.slice(0, 10), [filteredWorkflows]);

  const selectedWorkflow = usableWorkflows.find((w) => w.id.toString() === value);

  useEffect(() => {
    if (triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth);
    }
  }, []);

  const handleSelect = (workflow: GithubWorkflow) => {
    onChange(workflow.id.toString());
    setOpen(false);
    setSearchQuery("");
    setIsTyping(false);
  };

  const handleButtonClick = () => {
    if (disabled || isLoading) return;
    if (!isTyping) {
      setIsTyping(true);
      setOpen(true);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    }
  };

  // Handle popover close
  useEffect(() => {
    if (!open) {
      setIsTyping(false);
      setSearchQuery("");
    }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <div
          ref={triggerRef}
          onClick={handleButtonClick}
          className={`flex h-9 w-full items-center justify-between rounded-md border border-border bg-transparent px-3 py-2 text-sm cursor-pointer hover:bg-accent transition-colors ${
            disabled || isLoading ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          {isTyping ? (
            <>
              <Input
                ref={inputRef}
                placeholder="Search workflows..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-transparent border-0 h-auto p-0 focus-visible:ring-0 focus-visible:ring-offset-0"
              />
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </>
          ) : (
            <>
              <span className="truncate">
                {isLoading ? (
                  <span className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Loading and Validating...
                  </span>
                ) : selectedWorkflow ? (
                  <span className="text-foreground">{selectedWorkflow.name}</span>
                ) : (
                  <span className="text-muted-foreground">{placeholder}</span>
                )}
              </span>
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 bg-popover border-border"
        side="bottom"
        align="start"
        sideOffset={0}
        style={{
          width: triggerWidth > 0 ? `${triggerWidth}px` : "100%",
        }}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
        }}
      >
        {isLoading ? (
          <div className="p-3 text-xs text-muted-foreground flex items-center gap-2">
            <Loader2 className="h-3 w-3 animate-spin" />
            Loading and validating workflows...
          </div>
        ) : (
          <div
            className="max-h-[300px] overflow-y-scroll scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800"
            onWheel={(e) => {
              const container = e.currentTarget;
              const delta = e.deltaY;
              container.scrollTop += delta;
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <WorkflowResultsList
              workflows={limitedWorkflows}
              selectedWorkflowId={value}
              onSelect={handleSelect}
              emptyMessage={emptyMessage}
            />
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
